/**
 * PATH: packages/job-hub/src/jobs/tabs/job-qa/itp-export/startOnedriveConsent.ts
 * FILE_ID: JOBHUB-QA-ONEDRIVE-CONSENT-001
 * PURPOSE: Send the signed-in user to OneDrive consent for the mailbox in From.
 * Reused from: packages/email-hub/src/feature/hooks/useEmails.ts (buildOutlookAuthorizeQuery, useOutlookStatus.authorize)
 * Reused from: packages/tenant-shared/src/lib/oauthNavigation.ts (navigateToOAuthProvider)
 */
import { buildOutlookAuthorizeQuery } from '@quotech/email-hub/feature/hooks/useEmails'
import { navigateToOAuthProvider } from '@quotech/tenant-shared/lib/oauthNavigation'

export const QUOTECH_FROM_EMAIL = 'hello@quotech.ai'
export const ONEDRIVE_FROM_ERROR = 'OneDrive uses the mailbox selected in From.'

export interface FromAccount {
  id: number
  provider?: string
  email_address?: string
}

export interface SelectedMailbox {
  email: string
  accountId: number | null
  graph: boolean
}

function outlookMatch(sendable: readonly FromAccount[], email: string): FromAccount | undefined {
  const wanted = email.trim().toLowerCase()
  return sendable.find((row) =>
    row.provider === 'outlook' && (row.email_address || '').trim().toLowerCase() === wanted)
}

export function mailboxForFrom(
  from: string,
  sendable: readonly FromAccount[],
  userEmail: string,
): SelectedMailbox | { error: string } {
  if (from.startsWith('account:')) {
    const account = sendable.find((row) => `account:${row.id}` === from)
    if (!account || account.provider !== 'outlook') return { error: ONEDRIVE_FROM_ERROR }
    return { email: account.email_address || '', accountId: account.id, graph: true }
  }
  if (from === 'quotech') {
    const account = outlookMatch(sendable, QUOTECH_FROM_EMAIL)
    return { email: QUOTECH_FROM_EMAIL, accountId: account?.id ?? null, graph: false }
  }
  if (from === 'login') {
    const email = userEmail.trim()
    if (!email) return { error: ONEDRIVE_FROM_ERROR }
    const account = outlookMatch(sendable, email)
    return { email, accountId: account?.id ?? null, graph: false }
  }
  return { error: ONEDRIVE_FROM_ERROR }
}

export async function startOnedriveConsent(
  client: { get: (url: string) => Promise<{ data?: { authorization_url?: string } }> },
  loginHint: string,
): Promise<void> {
  const query = buildOutlookAuthorizeQuery(
    typeof window === 'undefined' ? '' : window.location.href,
    loginHint,
  )
  const { data } = await client.get(`/api/outlook/authorize/?${query}&features=onedrive`)
  if (data?.authorization_url) navigateToOAuthProvider(data.authorization_url)
}
